import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquareWarning, Plus, RefreshCw, Search } from "lucide-react";
import { formatINR, humanizeCode } from "@/lib/utils";
import type { ComplaintRecord, ComplaintStatus, ComplaintSummary } from "@/types";
import { ComplaintStatusBadge } from "@/components/complaints/ComplaintStatusBadge";
import { ComplaintActions } from "@/components/complaints/ComplaintActions";
import { RecordPhotosButton } from "@/components/attachments/RecordPhotosButton";
import { ComplaintFormDialog } from "@/components/complaints/ComplaintFormDialog";

const formatDate = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "-");

const FILTERS: Array<{ key: string; label: string; status: string }> = [
  { key: "OPEN", label: "Open", status: "OPEN,INVESTIGATING" },
  { key: "RESOLVED", label: "Resolved", status: "RESOLVED" },
  { key: "REJECTED", label: "Rejected", status: "REJECTED" },
  { key: "ALL", label: "All", status: "" },
];

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tracking-tight mt-1">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

export function ComplaintsPage() {
  const { error } = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("complaint.create");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("OPEN");
  const [summary, setSummary] = useState<ComplaintSummary | null>(null);
  const [dialog, setDialog] = useState<ComplaintRecord | "new" | null>(null);

  const list = usePagedList<ComplaintRecord>({
    deps: [search, filter],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      const status = FILTERS.find((f) => f.key === filter)?.status;
      if (status) qs.set("status", status);
      try {
        return unwrapPage<ComplaintRecord>(await api.get(`/api/Complaint/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load complaints");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  const loadSummary = () =>
    api
      .get("/api/Complaint/Summary")
      .then((r) => setSummary(unwrapObject<ComplaintSummary>(r)))
      .catch(() => setSummary(null));
  useEffect(() => {
    loadSummary();
  }, []);
  const refresh = () => {
    list.reload();
    loadSummary();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Complaints</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Shade, GSM, shrinkage, short quantity and other buyer complaints, tracked to resolution</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={list.loading} className="h-9 gap-1.5">
            <RefreshCw className={`size-3.5 ${list.loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canCreate && (
            <Button size="sm" onClick={() => setDialog("new")} className="h-9 gap-1.5 shadow-sm">
              <Plus className="size-4" />
              Log complaint
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label="Open complaints" value={String(summary.open)} hint={`${summary.by_status.INVESTIGATING || 0} being investigated`} />
          <Tile label="Open claims" value={formatINR(summary.open_claim_amount)} hint="Claimed by buyers, not settled" />
          <Tile label="Credit given" value={formatINR(summary.resolved_amount_given)} hint="On resolved complaints" />
          <Tile label="Days to resolve" value={summary.avg_days_to_resolve == null ? "-" : String(summary.avg_days_to_resolve)} hint={summary.by_type[0] ? `Most common: ${humanizeCode(summary.by_type[0].type)}` : "Average"} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by number, buyer, order or words..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={5} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Complaint</TableHead>
                <TableHead>Buyer / order</TableHead>
                <TableHead>Claim</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="flex items-start gap-2.5">
                      <div className="size-8 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
                        <MessageSquareWarning className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm">{humanizeCode(c.complaint_type)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          <code className="font-mono">{c.complaint_number}</code> · raised {formatDate(c.raised_on)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 max-w-[320px] break-words">{c.description}</div>
                        {(c.lot_number || c.affected_rolls) && (
                          <div className="text-[11px] font-medium mt-0.5">
                            {[c.lot_number ? `Lot ${c.lot_number}` : null, c.affected_rolls ? `Rolls ${c.affected_rolls}` : null].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{c.account?.name || "-"}</div>
                    {c.order && (
                      <Link to={`/orders/${c.order.id}`} className="text-xs text-primary hover:underline">
                        {c.order.order_number}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{c.claim_amount ? formatINR(c.claim_amount) : "-"}</TableCell>
                  <TableCell>
                    <ComplaintStatusBadge status={c.complaint_status as ComplaintStatus} />
                    {c.resolution_notes && (
                      <div className="text-[11px] text-muted-foreground mt-1 max-w-[220px] break-words">
                        {c.resolved_amount ? `${formatINR(c.resolved_amount)} · ` : ""}
                        {c.resolution_notes}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end items-center gap-1.5 flex-wrap">
                      <RecordPhotosButton type="COMPLAINT" recordId={c.id} count={c.photo_count} onChanged={refresh} title={`${c.complaint_number} · ${c.account?.name || ""}`} compact />
                      <ComplaintActions complaint={c} onChanged={refresh} onEdit={(x) => setDialog(x)} compact />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">
                    No complaints here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <ComplaintFormDialog target={dialog} onClose={() => setDialog(null)} onSaved={refresh} />
    </div>
  );
}
